import { useQueryClient } from 'react-query';
import { IconButton, Loading, SkeletonPlaceholder } from '@carbon/react';
import { Close } from '@carbon/react/icons';

import { useNotifications } from 'core/notifications';
import { useIsReadonly } from 'core/readonly';
import { useUser } from 'core/user/user';
import { SHARE_LEVEL } from 'models/shareLevel';
import useMutateUnshareProject from 'shared/api/projects/useMutateUnshareProject';
import useProjectSharesQuery, { createUseProjectSharesQueryKey } from 'shared/api/projects/useProjectSharesQuery';
import styles from 'theme/features/inputs/project-details-form.module.scss';

const SHARE_LEVEL_LABEL = {
  [SHARE_LEVEL.view]: 'Viewer',
  [SHARE_LEVEL.edit]: 'Editor'
};

const SharedWith = ({ projectId }) => {
  const queryClient = useQueryClient();

  const { user } = useUser();

  const isReadonly = useIsReadonly();

  const { showErrorNotification, showSuccessNotification } = useNotifications();

  const { data: shares, isLoading, isError } = useProjectSharesQuery(projectId);

  const unshareProjectMutation = useMutateUnshareProject({
    onError: () => showErrorNotification({ description: 'Failed to share the project' }),
    onSuccess: (_, { userId }) => {
      queryClient.setQueryData(createUseProjectSharesQueryKey(projectId), (prevShares) =>
        prevShares.filter((x) => x.userId !== userId)
      );
      showSuccessNotification({ description: 'Project shared successfully' });
    }
  });

  const handleUnshareProject = (userId) => {
    unshareProjectMutation.mutate({
      id: projectId,
      userId
    });
  };

  return (
    <div className={styles['shared-with']}>
      <strong>Shared with</strong>
      {isLoading || isError ? (
        <SkeletonPlaceholder className={styles['shared-with__skeleton']} />
      ) : !shares || shares.length === 0 ? (
        <p>This project has not been shared with anyone</p>
      ) : (
        <ul className={styles['shared-with__list']}>
          {shares.map((x) => (
            <li key={x.userId} className={styles['shared-with__item']}>
              <div>
                <p>{x.userName}</p>
                <span>{SHARE_LEVEL_LABEL[x.shareLevel]}</span>
              </div>
              {isReadonly || x.userId === user.id ? null : unshareProjectMutation.isLoading ? (
                <Loading small withOverlay={false} />
              ) : (
                <IconButton
                  label="Unshare"
                  align="left"
                  kind="ghost"
                  size="sm"
                  onClick={() => handleUnshareProject(x.userId)}
                >
                  <Close />
                </IconButton>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SharedWith;
